/**
 * dd-offer-events.js
 *
 * One tracker for the $25 opt-in reward and the First Order Promise.
 * Every reward and promise touch calls `window.ddOfferTrack(name, props)`
 * and the helper mirrors the event to both sinks the theme already uses:
 *
 *   window.dataLayer.push({ event: name, ...props })     (GTM / GA4)
 *   Shopify.analytics.publish('dd:' + name, props)       (web pixels)
 *
 * Event set (docs/contracts/account-unlock-trial-offer.md, "Events"):
 *   reward_prompt_shown      { trigger: quiet|pdp|cart|explicit }
 *   reward_prompt_dismissed  { via: close|not_now|backdrop|esc }
 *   reward_step              { step: 1|2|3|one_tap }
 *   reward_unlocked          { delivered }
 *   reward_apply_click
 *   reward_applied           (cart chip observed)
 *   reward_relock_seen
 *   promise_impression       { variant, surface }  deduped per pageview
 *   promise_link_click       { surface }
 *
 * Common props are merged automatically from window.ddRewardConfig:
 *   customer_id (hashed, never the raw id), primary_offer, prompt_count,
 *   cart_total (dollars), threshold_met.
 *
 * The helper never throws and never blocks: a sink that is missing or
 * broken is skipped. `promise_impression` is deduped per surface per
 * pageview so a re-rendered cart drawer does not double count.
 */
(function () {
  'use strict';

  if (window.ddOfferTrack) return;

  var seenImpressions = {};

  /* Small, stable, non-reversible hash so dashboards can count distinct
     customers without the raw Shopify id leaving the page. */
  function hashId(value) {
    var str = String(value || '');
    if (!str) return '';
    var h = 5381;
    for (var i = 0; i < str.length; i++) {
      h = ((h << 5) + h + str.charCodeAt(i)) | 0;
    }
    return 'c' + (h >>> 0).toString(36);
  }

  function commonProps() {
    var cfg = window.ddRewardConfig || {};
    var out = {};
    if (cfg.customerId) out.customer_id = hashId(cfg.customerId);
    if (cfg.primaryOffer) out.primary_offer = cfg.primaryOffer;
    if (typeof cfg.promptCount === 'number') out.prompt_count = cfg.promptCount;
    if (typeof cfg.cartTotalCents === 'number') {
      out.cart_total = Math.round(cfg.cartTotalCents) / 100;
    }
    if (typeof cfg.thresholdMet === 'boolean') out.threshold_met = cfg.thresholdMet;
    return out;
  }

  function track(name, props) {
    if (!name) return;
    var payload = Object.assign({}, commonProps(), props || {});

    if (name === 'promise_impression') {
      var key = String(payload.surface || '') + '|' + String(payload.variant || '');
      if (seenImpressions[key]) return;
      seenImpressions[key] = true;
    }

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, payload));
    } catch (e) {
      /* noop */
    }

    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish('dd:' + name, payload);
      }
    } catch (e) {
      /* noop */
    }

    try {
      document.dispatchEvent(new CustomEvent('dd:offer-event', { detail: { name: name, props: payload } }));
    } catch (e) {
      /* noop */
    }
  }

  window.ddOfferTrack = track;

  /* Promise impressions are declarative: any element carrying
     data-dd-promise-impression="<surface>" (and optional
     data-dd-promise-variant) is counted once when it first appears. The
     observer also catches section re-renders (cart drawer) because the
     dedupe key is per surface, not per element. */
  function observePromise() {
    var nodes = document.querySelectorAll('[data-dd-promise-impression]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.__ddPromiseSeen) continue;
      el.__ddPromiseSeen = true;
      track('promise_impression', {
        surface: el.getAttribute('data-dd-promise-impression') || 'unknown',
        variant: el.getAttribute('data-dd-promise-variant') || '',
      });
    }
  }

  /* reward_applied: the code is in the cart (Liquid saw it in
     cart.discount_codes). Once per session, on the first page that
     renders in the applied state, so the /discount redirect landing
     counts and later navigations do not. */
  function trackAppliedOnce() {
    var c = window.ddRewardConfig || {};
    if (c.state !== 'applied') return;
    var key = 'dd_reward_applied_tracked:' + String(c.customerId || '0');
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch (e) {
      /* private mode: fire anyway */
    }
    track('reward_applied', { code_in_cart: true });
  }

  function onReady() {
    trackAppliedOnce();
    observePromise();
    document.addEventListener('click', function (e) {
      var t = e.target && e.target.closest ? e.target.closest('[data-dd-promise-link]') : null;
      if (t) {
        track('promise_link_click', { surface: t.getAttribute('data-dd-promise-link') || 'unknown' });
      }
    });
    if (window.MutationObserver) {
      var scheduled = false;
      var mo = new MutationObserver(function () {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(function () {
          scheduled = false;
          observePromise();
        });
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady, { once: true });
  } else {
    onReady();
  }
})();
